import { BehaviorSubject } from 'rxjs';
import { addError } from './Error';
import { useSnackbar } from '../Context/SnackbarContext';
import { archiveCommentApi, createCommentApi, getCommentsApi, updateCommentApi } from '../Api/Comments/commentApi';
import { AxiosError } from 'axios';

export type Comment = {
  id: string;
  entity_id: string;
  entity_type: string;
  entity_name?: string;
  parent_comment_id?: number;
  comment_text: string;
  is_archived?: boolean;
  archived_by?: number;
  portal_user_id?: number;
  portal_user_name?: string;
  portal_user_email?: string;
  text_changed_at?: string;
  created_at?: string;
  updated_at?: string;
};

const commentsSubject = new BehaviorSubject<Comment[]>([]);
const commentsLoadingSubject = new BehaviorSubject<boolean>(false);
const commentsLoadFailedSubject = new BehaviorSubject<boolean>(false);
// Every comments panel shares this one thread, so each load starts it empty and only the latest may fill it.
let latestFetchId = 0;
let loadedThread = { entityId: '', entityType: '' };

export const getCommentSubject = (): BehaviorSubject<Comment[]> => commentsSubject;
export const getCommentLoadingSubject = (): BehaviorSubject<boolean> => commentsLoadingSubject;
export const getCommentLoadFailedSubject = (): BehaviorSubject<boolean> => commentsLoadFailedSubject;

const sameComment = (one: Comment, other: Comment) => String(one.id) === String(other.id);

// A post or a socket frame can answer after the panel has moved on to another record.
const onLoadedThread = (comment: Comment) =>
  comment.entity_id === loadedThread.entityId && comment.entity_type === loadedThread.entityType;

// A comment can reach the thread twice: in a load that already returned it, then over the socket or from its own post.
export const addCommentsToThread = (comments: Comment[]): Comment[] => {
  const thread = commentsSubject.getValue();
  const added = comments.filter(
    (comment) => onLoadedThread(comment) && !thread.some((held) => sameComment(held, comment))
  );
  if (added.length) commentsSubject.next([...thread, ...added]);
  return added;
};

export interface UseCommentsStoreReturn {
  fetchComments: (entityId: string, entityType: string) => Promise<void>;
  createComment: (comment: Comment) => Promise<boolean>;
  updateComment: (comment: Comment) => Promise<boolean>;
  archiveComment: (comment: Comment) => Promise<boolean>;
  setComments: (comments: Comment[]) => void;
}

type ErrorType = null | { message: string; errorResponse?: AxiosError } | { message: string };

// Only a refusal says the comment changed under the write, such as an archive from another session overtaking an edit.
const isRefusal = (error: ErrorType): boolean => {
  const status = error && 'errorResponse' in error ? error.errorResponse?.response?.status : undefined;
  return status === 403 || status === 404;
};

export const useCommentsStore = (): UseCommentsStoreReturn => {
  const { setSnack } = useSnackbar();

  const fetchComments = async (entityId: string, entityType: string): Promise<void> => {
    const fetchId = ++latestFetchId;
    loadedThread = { entityId, entityType };
    commentsSubject.next([]);
    commentsLoadFailedSubject.next(false);
    commentsLoadingSubject.next(true);

    const { data, error } = await getCommentsApi(entityId, entityType);
    if (fetchId !== latestFetchId) return;

    // The answer can predate a comment that the socket or a post added while it was on its way.
    const arrivedMeanwhile = commentsSubject.getValue().filter((held) => !data?.some((c) => sameComment(c, held)));
    if (data) commentsSubject.next([...data, ...arrivedMeanwhile]);
    if (error) {
      addError('comment', error.message);
      commentsLoadFailedSubject.next(true);
    }

    commentsLoadingSubject.next(false);
  };

  const createComment = async (comment: Comment): Promise<boolean> => {
    const { data, error } = await createCommentApi(comment);
    if (data) {
      addCommentsToThread([data]);
      setSnack({ message: 'Comment saved successfully', type: 'success', open: true });
    }

    if (error) {
      setSnack({ message: 'Error saving comment', type: 'error', open: true });
    }
    return !!data;
  };

  const refreshComment = (data: Comment | null, success_msg: string, error: ErrorType, error_msg: string) => {
    if (data) {
      const currentState = [...getCommentSubject().getValue()];
      const updatedComments = currentState.map((com: Comment) => (com.id === data.id ? data : com));
      setComments(updatedComments);
      setSnack({ message: success_msg, type: 'success', open: true });
    }

    if (error) {
      setSnack({ message: error_msg, type: 'error', open: true });
    }
  };

  // The thread stays on show until the answer, so a failed read only flags it and an open composer keeps its text.
  const rereadThreadOf = async (comment: Comment) => {
    if (!onLoadedThread(comment)) return;
    const fetchId = latestFetchId;
    const shown = commentsSubject.getValue();
    const { data, error } = await getCommentsApi(comment.entity_id, comment.entity_type);
    if (fetchId !== latestFetchId) return;

    if (error) addError('comment', error.message);
    commentsLoadFailedSubject.next(!!error);
    if (!data) return;

    const arrivedMeanwhile = commentsSubject
      .getValue()
      .filter((held) => !shown.some((old) => sameComment(old, held)) && !data.some((c) => sameComment(c, held)));
    commentsSubject.next([...data, ...arrivedMeanwhile]);
  };

  const updateComment = async (comment: Comment) => {
    const { data, error } = await updateCommentApi(comment);

    const success_msg = 'Comment updated successfully';
    const error_msg = 'Error updating comment';
    refreshComment(data, success_msg, error, error_msg);
    if (isRefusal(error)) await rereadThreadOf(comment);
    return !!data;
  };

  const archiveComment = async (comment: Comment) => {
    const { data, error } = await archiveCommentApi(comment.id);

    const success_msg = 'Comment deleted successfully';
    const error_msg = 'Error deleting comment';
    refreshComment(data, success_msg, error, error_msg);
    if (isRefusal(error)) await rereadThreadOf(comment);
    return !!data;
  };

  const setComments = (comments: Comment[]) => commentsSubject.next(comments);

  return {
    fetchComments,
    createComment,
    updateComment,
    archiveComment,
    setComments
  };
};
